import { format, isValid, parseISO } from 'date-fns';

import {
  type DateString,
  type DateTimeString,
  ZodBrandedDateString,
  ZodBrandedDateTimeString,
} from '@Common/types/zod/zod-branded-date';

const OFFSET_REGEX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export function isDateString(value: unknown): value is DateString {
  // the schema also converts a Date, which is not a DateString itself
  return typeof value === 'string' && ZodBrandedDateString.safeParse(value).success;
}

/**
 * Only canonical values pass: `2026-10-15T08:30:00.000Z`, not `2026-10-15T08:30:00Z`.
 */
export function isDateTimeString(value: unknown): value is DateTimeString {
  if (typeof value !== 'string') {
    return false;
  }
  const result = ZodBrandedDateTimeString.safeParse(value);
  return result.success && result.data === value;
}

function dateToDateString(value: Date): DateString | null {
  if (!isValid(value)) {
    return null;
  }
  const day = format(value, 'yyyy-MM-dd');
  return isDateString(day) ? day : null;
}

function dateToDateTimeString(value: Date): DateTimeString | null {
  if (!isValid(value)) {
    return null;
  }
  // years outside 0000-9999 use the extended `+010000-01-01` format, which is not a DateTimeString
  const instant = value.toISOString();
  return isDateTimeString(instant) ? instant : null;
}

/**
 * Converts a value to a calendar day, or returns `null` when it is empty or not a valid date.
 * - `YYYY-MM-DD` string: returned as is
 * - ISO string with an offset (`Z`, `+02:00`): its **UTC** calendar day
 * - `Date`: its **local** calendar day. A `Date` from a date picker, date-fns or a DB `timestamp`
 *   column holds local wall-clock time.
 *
 * Strings without an offset (`2026-10-15T10:00:00`) are ambiguous and return `null`.
 */
export function tryToDateString(value: unknown): DateString | null {
  if (value instanceof Date) {
    return dateToDateString(value);
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (isDateString(value)) {
    return value;
  }
  if (!OFFSET_REGEX.test(value)) {
    return null;
  }
  const instant = new Date(value);
  if (!isValid(instant)) {
    return null;
  }
  const day = instant.toISOString().slice(0, 10);
  return isDateString(day) ? day : null;
}

/**
 * Same as `tryToDateString`, but throws a `TypeError` for invalid values.
 */
export function toDateString(value: Date | string): DateString {
  const day = tryToDateString(value);
  if (day === null) {
    throw new TypeError(`Invalid calendar day: ${String(value)}`);
  }
  return day;
}

/**
 * Converts a value to a UTC ISO string, or returns `null` when it is empty or not a valid instant.
 * Strings without an offset (`2026-10-15T10:00:00`, `2026-10-15`) are ambiguous and return `null`.
 */
export function tryToDateTimeString(value: unknown): DateTimeString | null {
  if (value instanceof Date) {
    return dateToDateTimeString(value);
  }
  if (typeof value !== 'string' || !OFFSET_REGEX.test(value)) {
    return null;
  }
  return dateToDateTimeString(new Date(value));
}

/**
 * Same as `tryToDateTimeString`, but throws a `TypeError` for invalid values.
 */
export function toDateTimeString(value: Date | string): DateTimeString {
  const instant = tryToDateTimeString(value);
  if (instant === null) {
    throw new TypeError(`Invalid instant: ${String(value)}`);
  }
  return instant;
}

/**
 * Local midnight of the given calendar day, for date-fns calculations.
 */
export function dateStringToLocalDate(value: DateString): Date {
  return parseISO(value);
}

/**
 * Local midnight of the value's calendar day (see `tryToDateString`), or `null`.
 * Use it for date-fns calculations on values that are not typed yet, e.g. form values.
 */
export function tryToLocalDate(value: unknown): Date | null {
  const day = tryToDateString(value);
  return day === null ? null : dateStringToLocalDate(day);
}

/**
 * Today's local calendar day.
 * Unlike `new Date().toISOString()`, it does not return yesterday between 00:00 and 02:00 in Paris.
 */
export function todayDateString(): DateString {
  return toDateString(new Date());
}

export function nowDateTimeString(): DateTimeString {
  return toDateTimeString(new Date());
}
